import React, { useState } from 'react';
import t from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';

import useQuery from '../../../utils/useQuery';
import { validateEmail, validateLength } from '../../../utils/validation';
import { appIdentity, baseUrl, loginHttp } from '../../../utils/http';
import { genericErrorMessage } from '../../../utils/lang/commonErrors';
import { CAPTCHA_KEY, getCaptchaToken } from '../../../utils/captchaHelper';

import Checkbox from '../fields/Checkbox';
import SubmitButton from '../fields/SubmitButton';
import PasswordInput from './PasswordInput';

import googleIcon from '../../../../assets/icons/feedback/google-icon.svg';
import facebookIcon from '../../../../assets/icons/feedback/facebook-icon.svg';
import appleIcon from '../../../../assets/icons/feedback/apple-icon.svg';

import styles from '../ReferralSignUpForm.scss';

const ReferralSignUp = ({ disabled, onSignUp }) => {
  const { email } = useQuery();
  const history = useHistory();

  const intl = useIntl();

  const [emailValue, setEmailValue] = useState(email || '');
  const onEmailChange = (e) => setEmailValue(e.target.value);

  const [passValue, setPassValue] = useState('');
  const onPassChange = (e) => setPassValue(e.target.value);

  const [isNewsletterSub, setNewsletterSub] = useState(false);

  const [isLoading, setLoading] = useState(false);

  const [formErrors, setFormErrors] = useState({});
  const onSubmit = (e) => {
    e.preventDefault();
    setFormErrors({});
    setLoading(true);
    const emailError = validateEmail(emailValue);
    const passError = validateLength(passValue, 8, 21);

    if (emailError || passError) {
      setFormErrors({
        email: emailError,
        pass: passError
      });
      setLoading(false);
    } else {
      getCaptchaToken()
        .then((token) => loginHttp('/v1/account/sign-up', {
          email: emailValue,
          password: passValue,
          receive_newsletters: isNewsletterSub,
          recaptcha_token: token,
          recaptcha_site_key: CAPTCHA_KEY
        })
          .then(
            () => {
              onSignUp();
              history.push('/profile');
            },
            (data) => {
              if (data.code === 6) {
                history.push(`/enter/email/already-used?email=${encodeURIComponent(emailValue)}`);
              } else if (data.code === 3 && data.message === 'email is invalid') {
                setFormErrors({
                  email: intl.formatMessage({
                    id: 'signup.error.emailDoesNotExist',
                    defaultMessage: 'Email address doesn’t exist',
                    description: 'Error message show as the result of a failed check of the inputted email against email verification service'
                  })
                });
              } else {
                console.error('Referral sign Up request failed', data);
                setFormErrors({
                  pass: intl.formatMessage(genericErrorMessage)
                });
              }
              setLoading(false);
            })
        );
    }
  };

  const onSocialLoginClick = (title) => {
    onSignUp();
    window.location = `${baseUrl}/v1/account/oauth/${title.toLocaleLowerCase()}/login?app-identity=${appIdentity}`;
  };

  return (
    <form className={styles.form} onSubmit={onSubmit}>
      <fieldset disabled={disabled || isLoading}>
        <h2 className={styles.formTitle}>
          <FormattedMessage
            id="refSignUp.signUp.title"
            defaultMessage="Create your account"
            description="Title of the sign up form"
          />
        </h2>

        <div className={styles.formLine}>
          <label className={`${styles.label} ${formErrors.email ? styles.error : ''}`}>
            <FormattedMessage
              id="refSignUp.signUp.emailLabel"
              defaultMessage="Email"
              description="Label of the email input"
            />
            <input
              type="email"
              name="email"
              autoComplete="email"
              value={emailValue}
              onChange={onEmailChange}
              placeholder="example@gmail.com"
            />

            <span className={styles.errorText}>{formErrors.email}</span>
          </label>

          <PasswordInput
            onChange={onPassChange}
            passValue={passValue}
            label={intl.formatMessage({
              id: 'refSignUp.signUp.passwordLabel',
              defaultMessage: 'Password',
              description: 'Label of the new password input'
            })}
            placeholder={intl.formatMessage({
              id: 'refSignUp.signUp.passwordPlaceholder',
              defaultMessage: 'Create a password',
              description: 'Placeholder for the referral sing up password field'
            })}
            autoComplete="new-password"
            error={formErrors.pass}
            name="password"
          />
        </div>

        <SubmitButton
          text={intl.formatMessage({
            id: 'refSignUp.signUp.submit',
            defaultMessage: 'Create account',
            description: 'Submit button of the sign up form'
          })}
          className={styles.submitButton}
          isLoading={isLoading}
        />

        <Checkbox
          value={isNewsletterSub}
          onChange={setNewsletterSub}
          label={intl.formatMessage({
            id: 'refSignUp.signUp.newsletterSubscriptionCheckbox',
            defaultMessage: 'Subscribe to our digest to learn useful privacy tips',
            description: 'Label of the checkbox, that will subscribe to the email newsletter if checked during sign up'
          })}
          largeLabel
          isNoError
          className={styles.newsletterSub}
        />

        <div className={styles.otherLogins}>
          <span className={styles.otherLoginsLabel}>
            <FormattedMessage
              id="refSignUp.signUp.alternativeSignUpsLabel"
              defaultMessage="or continue with"
              description="Label for the alternative sign up sections, i.e. Google or Facebook instead of email&pass"
            />
          </span>

          <div className={styles.otherLoginsButtons}>
            <button onClick={() => onSocialLoginClick('Google')} type="button">
              <img
                src={googleIcon}
                width="24"
                height="24"
                alt={intl.formatMessage({
                  id: 'refSignUp.signUp.signUpWithGoogle',
                  defaultMessage: 'Sign up with Google',
                  description: 'Button, uses user’s Google account for sign up'
                })}
              />
            </button>
            <button onClick={() => onSocialLoginClick('Facebook')} type="button">
              <img
                src={facebookIcon}
                width="24"
                height="24"
                alt={intl.formatMessage({
                  id: 'refSignUp.signUp.signUpWithFacebook',
                  defaultMessage: 'Sign up with Facebook',
                  description: 'Button, uses user’s Facebook account for sign up'
                })}
              />
            </button>
            <button onClick={() => onSocialLoginClick('Apple')} type="button">
              <img
                src={appleIcon}
                width="24"
                height="24"
                alt={intl.formatMessage({
                  id: 'refSignUp.signUp.signUpWithApple',
                  defaultMessage: 'Sign up with Apple',
                  description: 'Button, uses user’s AppleID account for sign up'
                })}
              />
            </button>
          </div>
        </div>
      </fieldset>
    </form>
  );
};
ReferralSignUp.propTypes = {
  disabled: t.bool,
  onSignUp: t.func.isRequired
};

export default ReferralSignUp;
